import type { QiGroup } from './manifest';

/**
 * I-3: inline collection criteria for the Quarter workspace.
 *
 * Source: National Aged Care Quality Indicator Program Manual, Part A, September 2026
 * (lane source copy qi-manual-part-a-202609.txt, SHA256 5e1f9369…41f9993; see
 * docs/source-of-truth/qi-ledger.md "Sources and verification boundary"). Each entry
 * paraphrases the chapter's "Measurements and assessments" steps (§N.3) and its
 * exclusions (§N.3.2). Text is a reading aid for staff, never a rule the ledger applies;
 * the aggregation in aggregate.ts remains the authority. Wording amended after an independent
 * Codex audit against the Manual (27 Oct 2026); the product owner ships it on that audit.
 */
export const QI_MANUAL_EDITION = 'QI Program Manual Part A (September 2026)';

export interface QiCollectionCriteria {
  /** Manual chapter sections, e.g. "§8.3". */
  sections: string;
  /** How and when the data is collected (about 300 characters). */
  collection: string;
  /** Who is excluded and reported under additional reporting. */
  exclusions: string;
}

export const QI_COLLECTION_CRITERIA: Readonly<
  Record<Exclude<QiGroup, 'QIT'>, QiCollectionCriteria>
> = {
  PI: {
    sections: '§8.3',
    collection:
      'Once each quarter, on or around the same date, do a full-body observation of every resident, ideally during routine personal care, after seeking consent. Record each resident with one or more pressure injuries against each of the six stages, wherever the injury was acquired.',
    exclusions:
      'Residents who withheld consent to pressure-injury observation throughout the quarter, or were absent from the home throughout the quarter. Record these exclusions under additional reporting.',
  },
  PR: {
    sections: '§9.3',
    collection:
      'Choose an unpredictable collection date each quarter. The collection period is that date and the two days before, the same three days for everyone. From existing records, record whether each resident was subject to a restrictive practice other than chemical restraint, and whether it was only a secure area.',
    exclusions: 'Residents absent from care for the entire three-day collection period.',
  },
  UPWL: {
    sections: '§10.3 (significant), §11.3 (consecutive)',
    collection:
      'Weigh monthly using calibrated scales at similar times and in similar clothing. Significant loss compares previous-quarter finishing and current finishing weights; consecutive loss uses all four weights. Do not count planned loss supported by a written strategy and/or ongoing records.',
    exclusions:
      'Exclude end-of-life care or missing required weights. Significant loss: consent refusal on the finishing date; requires previous and finishing weights. Consecutive loss: refusal on any collection date; requires all four weights. Explain missing weights.',
  },
  FMI: {
    sections: '§12.3',
    collection:
      'Within 21 days after the quarter ends, review every resident’s records for the whole quarter. Record whether each resident had one or more falls at the home, and whether any fall caused a major injury.',
    exclusions:
      'Residents absent from care for the entire quarter. Falls while away from the home and not under direct staff supervision are not counted.',
  },
  MM: {
    sections: '§13.3 (polypharmacy), §14.3 (antipsychotics)',
    collection:
      'Polypharmacy: on the collection date, count qualifying medicines, including non-prescription medicines; exclude skin/wound preparations, supplements, short-term and PRN medicines. Count different doses of the same medicine once. Antipsychotics: vary the date between quarters, from week two to quarter-end; conceal it from direct-care staff, who must not conduct collection. Review that date and six preceding days for antipsychotics received, including PRN, and check recipients’ records for diagnosed psychosis.',
    exclusions:
      'Polypharmacy: residents in hospital on the collection date. Antipsychotics: residents in hospital for the whole seven-day collection period.',
  },
  ADL: {
    sections: '§15.3',
    collection:
      'Complete every Barthel Index item around the same time each quarter, using actual performance over the previous 24–48 hours (longer for relevant items). Record the total, previous-quarter zero scores, and any decline of at least one point.',
    exclusions:
      'Residents receiving end-of-life care for the entire quarter, absent for the entire quarter, or with no previous quarter ADL total recorded. Explain in the comment why the previous assessment was not recorded.',
  },
  IAD: {
    sections: '§16.3',
    collection:
      'Assess every resident for incontinence around the same time each quarter, seeking consent. For residents with incontinence, seek consent for IAD assessment; record IAD regardless of acquisition location and each applicable Ghent category: 1A, 1B, 2A or 2B.',
    exclusions:
      'Residents absent for the entire quarter. Residents without incontinence are excluded from the dermatitis assessment only.',
  },
  HP: {
    sections: '§17.3',
    collection:
      'Within 21 days after the quarter ends, review every resident’s records for the whole quarter. Record residents with one or more emergency department presentations, and with one or more presentations or hospital admissions.',
    exclusions: 'Residents absent from care for the entire quarter.',
  },
  WF: {
    sections: '§18.3',
    collection:
      'Within 21 days after quarter-end, review the listed staff roles, including nursing assistants. Count any hours in the quarter preceding the reported quarter; identify those with ≥120 hours then, and among them those with ≥60 consecutive days without work during the reported quarter.',
    exclusions: 'None. The Manual lists no workforce exclusions.',
  },
  CEI: {
    sections: '§19.3',
    collection:
      'Assess around the same time each quarter using the relevant QCE-ACC version. Arrange interviewer assistance where needed and proxy completion where self/interview completion is impossible. Record result bands by assessment method.',
    exclusions:
      'Residents absent for the entire quarter, or who chose not to complete the assessment.',
  },
  QOL: {
    sections: '§20.3',
    collection:
      'Assess around the same time each quarter using the relevant QOL-ACC version. Arrange interviewer assistance where needed and proxy completion where self/interview completion is impossible. Record result bands by assessment method.',
    exclusions:
      'Residents absent for the entire quarter, or who chose not to complete the assessment.',
  },
  AH: {
    sections: '§23.3',
    collection:
      'Review each resident’s care and services plan for allied health services recommended during the quarter, and record by discipline whether the recommended services were received.',
    exclusions: 'Residents absent from care for the entire quarter.',
  },
};
